import { useEffect, useMemo, useState } from "react";
import { BANNER } from "./engine";
import { cx, prefersReducedMotion } from "./util";

const SCRAMBLE = "#@%*+=-:.";
const SOLID = /[█▀-▟]/;
// Column ranges of the six letters in the default BANNER (g r a s p d), for per-letter hover.
const BANNER_KEYS = [[0, 9], [9, 17], [17, 25], [25, 33], [33, 41], [41, 49]];

function layer(lines, isSolid, prog) {
  return lines.map((ln, y) => {
    let s = "";
    for (let x = 0; x < ln.length; x++) {
      const c = ln.charAt(x);
      if (c === " " || SOLID.test(c) !== isSolid) { s += " "; continue; }
      const edge = prog * 1.25 - 0.15 + ((x * 7 + y * 13) % 5) * 0.03;
      if (prog < 1 && x / ln.length > edge) {
        s += prog * 1.25 > x / ln.length - 0.3 ? SCRAMBLE.charAt((x + y + Math.floor(prog * 40)) % SCRAMBLE.length) : " ";
      } else s += c;
    }
    return s;
  }).join("\n");
}

/**
 * Block-letter wordmark. Solid blocks in mint (or ink), box-drawing shadows in ink-muted.
 * Each letter is a "key": hovering one slides its solid face onto its own shadow
 * (the offset is --key-dx/--key-dy in ascii.css) and the shadow drops out, like a depressed keycap.
 */
export default function AsciiBanner({ art = BANNER, tone = "mint", reveal = false, duration = 1100, label = "graspd", className, style }) {
  const lines = useMemo(() => art.split("\n"), [art]);
  const [prog, setProg] = useState(reveal && !prefersReducedMotion() ? 0 : 1);

  useEffect(() => {
    if (prog >= 1) return;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      setProg(p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const faceCls = tone === "ink" ? "gd-l-ink" : "gd-l-mint";
  const shadow = layer(lines, false, prog).split("\n"), face = layer(lines, true, prog).split("\n");
  const slice = (rows, [a, b]) => rows.map((r) => r.slice(a, b).padEnd(b - a, " ")).join("\n");
  const keys = art === BANNER ? BANNER_KEYS : null;

  return (
    <div className={cx("gd-ascii", keys && "gd-banner-keys", className)} style={style} role="img" aria-label={label}>
      {keys ? keys.map((k, i) => (
        <span key={i} className="gd-key" aria-hidden="true">
          <pre className={cx("gd-l-muted", "gd-key-side")}>{slice(shadow, k)}</pre>
          <pre className={cx(faceCls, "gd-key-face")}>{slice(face, k)}</pre>
        </span>
      )) : (
        <>
          <pre className="gd-l-muted" aria-hidden="true">{shadow.join("\n")}</pre>
          <pre className={faceCls} aria-hidden="true">{face.join("\n")}</pre>
        </>
      )}
    </div>
  );
}
